// BuildingFootprint_Pure.ts — PURE geometry helpers for the Buildings Set1 footprint layer
// (§3.4). No mobx-graphlink / three / DOM / MapObject imports, so the Node vitest lane can import
// this directly. The DB-dependent "which points does this room use" resolution lives in
// `BuildingAccessors.ts`; this file does the geometry post-processing that is identical regardless
// of source (shape/mesh/path): winding normalization, closure, wall-segment derivation, relative
// pose transforms, and the AABB used for roof rectilinearization.
//
// Coordinate convention (matches the world): y+ is up; positive object rotation is CLOCKWISE
// degrees about Z. A CCW-normalized footprint (interior on the left when traversing) has its
// outward normal pointing to the RIGHT of the travel direction.
import type {Vec2Simple} from "../../../../Utils/ThreeJS/VectorStructs.js";
import type {WallSegment} from "./BuildingGeometry.js";

/** Signed polygon area (positive = CCW when y+ is up). Standard shoelace. */
export function SignedPolygonArea(points: Vec2Simple[]): number {
	let area = 0;
	for (let i = 0; i < points.length; i++) {
		const [x1, y1] = points[i];
		const [x2, y2] = points[(i + 1) % points.length];
		area += x1 * y2 - x2 * y1;
	}
	return area / 2;
}

/** Normalize a footprint for building use: drop trailing duplicate of the first point, and re-wind
 *  to CCW (so the interior face is consistently on the left). `closed=true` (default) appends the
 *  first point at the end so consumers can treat the array as a closed loop; `closed=false` returns
 *  the CCW-re-wound loop WITHOUT the closing duplicate. Returns a new array (input untouched). */
export function NormalizeFootprint(points: Vec2Simple[], closed = true): Vec2Simple[] {
	let pts = points.map(a=>[a[0], a[1]] as Vec2Simple);
	// Drop a trailing exact duplicate of the first point (legacy sources sometimes close the loop).
	while (pts.length >= 2) {
		const [x0, y0] = pts[0];
		const [lx, ly] = pts[pts.length - 1];
		if (Math.abs(lx - x0) < 1e-9 && Math.abs(ly - y0) < 1e-9) pts.pop();
		else break;
	}
	if (pts.length >= 3) {
		// Re-wind to CCW (positive signed area).
		if (SignedPolygonArea(pts) < 0) {
			const reversed: Vec2Simple[] = [];
			for (let i = pts.length - 1; i >= 0; i--) reversed.push(pts[i]);
			pts = reversed;
		}
	}
	if (closed && pts.length >= 1) {
		const [x0, y0] = pts[0];
		const [lx, ly] = pts[pts.length - 1];
		if (Math.abs(lx - x0) > 1e-9 || Math.abs(ly - y0) > 1e-9) pts.push([x0, y0]);
	}
	return pts;
}

/** Axis-aligned bounding box of a footprint. */
export function FootprintAABB(points: Vec2Simple[]): {minX: number, minY: number, maxX: number, maxY: number} {
	let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
	for (const [x, y] of points) {
		if (x < minX) minX = x;
		if (y < minY) minY = y;
		if (x > maxX) maxX = x;
		if (y > maxY) maxY = y;
	}
	return {minX, minY, maxX, maxY};
}

/** The four corners of a footprint's AABB (rectilinear replacement for the roof union). */
export function AABBPolygon(bounds: {minX: number, minY: number, maxX: number, maxY: number}): Vec2Simple[] {
	return [[bounds.minX, bounds.minY], [bounds.minX, bounds.maxY], [bounds.maxX, bounds.maxY], [bounds.maxX, bounds.minY]];
}

/** True when a rotation (degrees) is a multiple of 90° (i.e. the rectilinear roof union is exact). */
export function IsRectilinearRotation(rotationDeg: number): boolean {
	const r = ((rotationDeg % 360) + 360) % 360; // normalize to [0, 360)
	return Math.abs(r % 90) < 1e-6;
}

// ---------------------------------------------------------------------------
// Wall segments
// ---------------------------------------------------------------------------
/** Build the wall-segment list from a CLOSED CCW footprint (interior on the left). Each segment
 *  runs interior[start] → interior[end]; `normal` is the unit OUTWARD normal (perpendicular to the
 *  travel direction, pointing away from the interior). The footprint must already be CCW +
 *  closed (see `NormalizeFootprint`); this function does not re-normalize. */
export function ComputeWallSegments(closedPoints: Vec2Simple[]): WallSegment[] {
	const segments: WallSegment[] = [];
	for (let i = 0; i < closedPoints.length - 1; i++) {
		const start = closedPoints[i];
		const end = closedPoints[i + 1];
		const dx = end[0] - start[0];
		const dy = end[1] - start[1];
		const length = Math.sqrt(dx * dx + dy * dy);
		if (length < 1e-9) continue; // skip degenerate zero-length edges
		// Outward normal for a CCW polygon = perp of the direction, rotated to the RIGHT of travel.
		const n = [(dy) / length, (-dx) / length] as Vec2Simple;
		segments.push({start, end, length, normal: n});
	}
	return segments;
}

// ---------------------------------------------------------------------------
// Relative pose transforms (room-local / opening-local → building-local)
// ---------------------------------------------------------------------------
/** A 2D rigid pose: clockwise degrees + translation. Used to express a child object's local
 *  content in its parent's frame. */
export type Pose2D = {rotationDeg: number; position: Vec2Simple};

/** Transform `points` (in a child's local frame) into a higher frame by applying `chain` of poses
 *  in order (child pose first, then each ancestor up to the stop object). Each pose applies
 *  `rotate(p, -rotationDeg)` then `translate(p, position)` — the SAME sign convention as
 *  `ResolveObjPosInWorld` (positive stored rotation = clockwise). */
export function TransformPointsToFrame(points: Vec2Simple[], chain: Pose2D[]): Vec2Simple[] {
	let result = points;
	for (const pose of chain) {
		const rotRad = -pose.rotationDeg * (Math.PI / 180);
		const cos = Math.cos(rotRad), sin = Math.sin(rotRad);
		const px = pose.position[0], py = pose.position[1];
		result = result.map(([x, y])=>
			[x * cos - y * sin + px, x * sin + y * cos + py] as Vec2Simple);
	}
	return result;
}
